import { decodeBase64Url } from './walletPasskeyCrypto'
import {
    passkeyBrowserUnavailableReason,
    serializeWalletPasskeyCredential,
    type PasskeyRequestOptionsJSON,
} from './walletPasskeyWebAuthn'

// "Sign in with passkey": the wallet-backup passkey is discoverable and tied to the
// account, so the browser offers it without a username.

export interface PasskeySignInOptions {
    request_id: string
    public_key: PasskeyRequestOptionsJSON
}

export interface PasskeySignInResult {
    token: {
        'access-token': string | null
        expiry: string | null
        uid: string | null
        client: string | null
    }
    data: Record<string, unknown> | undefined
}

export function passkeySignInUnavailableReason(): string | null {
    return passkeyBrowserUnavailableReason()
}

async function passkeyPost<T>(
    path: string,
    body: unknown,
): Promise<{ data: T; response: Response }> {
    const response = await fetch(`${window.knovApiUrl}${path}`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        cache: 'no-store',
        body: JSON.stringify(body),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
        throw new Error(
            data?.errors?.[0] || data?.error || 'Passkey sign-in failed. Please try again.',
        )
    }
    return { data, response }
}

/** Fetch before the click: Safari needs navigator.credentials.get to run in the gesture. */
export async function preparePasskeySignIn(): Promise<PasskeySignInOptions> {
    return (await passkeyPost<PasskeySignInOptions>('/passkey_session/options', {})).data
}

export async function signInWithPasskey(
    options: PasskeySignInOptions,
): Promise<PasskeySignInResult> {
    const reason = passkeyBrowserUnavailableReason()
    if (reason) throw new Error(reason)
    const credential = (await navigator.credentials.get({
        publicKey: {
            rpId: options.public_key.rpId,
            timeout: options.public_key.timeout,
            challenge: decodeBase64Url(options.public_key.challenge),
            // Empty: any of this site's discoverable passkeys may answer.
            allowCredentials: [],
            userVerification: 'required',
        },
    })) as PublicKeyCredential | null
    if (!credential) throw new Error('Passkey sign-in was cancelled.')
    const { data, response } = await passkeyPost<{ data?: Record<string, unknown> }>(
        '/passkey_session',
        {
            request_id: options.request_id,
            credential: serializeWalletPasskeyCredential(credential),
        },
    )
    return {
        token: {
            'access-token': response.headers.get('access-token'),
            expiry: response.headers.get('expiry'),
            uid: response.headers.get('uid'),
            client: response.headers.get('client'),
        },
        data: data?.data,
    }
}
